'use client'

import * as Sentry from '@sentry/nextjs'
import posthog, { type BeforeSendFn, type CaptureResult } from 'posthog-js'

import {
  getFlowFailureCause,
  isInternalUserId,
  type AnalyticsEnvironment,
  type AuthMethod,
  type CheckoutStartedProperties,
  type FlowFailedProperties,
  type RegistrationEntryPoint,
} from './events'

interface PostHogClient {
  capture(eventName: string, properties?: Record<string, unknown>, options?: { timestamp?: Date }): unknown
  identify(distinctId: string, userProperties?: Record<string, unknown>): void
  init(
    projectToken: string,
    options: {
      api_host: string
      autocapture: false
      before_send: BeforeSendFn
      capture_pageview: false
      disable_session_recording: true
    }
  ): unknown
  reset(): void
}

interface ProductAnalyticsDependencies {
  captureException(error: unknown): string
  identityPersistence: {
    clearUserId(): void
    getResetPending(): boolean
    getResetPendingCookie(): boolean
    getUserId(): string | undefined
    setResetPending(resetPending: boolean): void
    setResetPendingCookie(resetPending: boolean): void
    setUserId(userId: string): void
  }
  posthog: PostHogClient
}

export interface ProductAnalyticsConfig {
  appVersion: string
  enabled: boolean
  environment: AnalyticsEnvironment
  errorReportingEnabled: boolean
  host: string
  isImpersonating: boolean
  projectToken: string
}

export const isProductAnalyticsConfigured = (config: ProductAnalyticsConfig): boolean =>
  config.enabled && Boolean(config.host) && Boolean(config.projectToken)

interface CommonEventProperties {
  $geoip_disable: true
  app_version: string
  environment: AnalyticsEnvironment
  event_schema_version: 1
  source: 'web'
}

const toPathname = (value: string): string => {
  try {
    return new URL(value, 'https://evirma.invalid').pathname
  } catch {
    return value.split(/[?#]/)[0] || '/'
  }
}

const UUID_PATH_SEGMENT_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const NUMERIC_PATH_SEGMENT_PATTERN = /^\d+$/
const EMAIL_PATH_SEGMENT_PATTERN = /^[^@\s/]+@[^@\s/]+$/
const ENCODED_PATH_FRAGMENT_PATTERN = /%[0-9a-f]{2}/i
const MAX_PATH_SEGMENT_DECODE_PASSES = 2
const KNOWN_DYNAMIC_PATHS: ReadonlyArray<[RegExp, string]> = [
  [/^\/auth\/recovery\/[^/]+\/?$/, '/auth/recovery/__id__'],
  [/^\/my\/ctr\/article\/.+$/, '/my/ctr/article/__id__'],
  [/^\/my\/pj\/root\/[^/]+\/?$/, '/my/pj/root/__id__'],
  [/^\/my\/pj\/(?!root(?:\/|$))[^/]+\/?$/, '/my/pj/__id__'],
  [/^\/my\/project\/settings\/subscription\/[^/]+\/?$/, '/my/project/settings/subscription/__id__'],
]

const sanitizePathSegment = (segment: string): string => {
  let decodedSegment = segment
  try {
    for (let pass = 0; pass < MAX_PATH_SEGMENT_DECODE_PASSES; pass += 1) {
      const nextDecodedSegment = decodeURIComponent(decodedSegment)
      if (nextDecodedSegment === decodedSegment) {
        break
      }
      decodedSegment = nextDecodedSegment
    }
  } catch {
    return '__id__'
  }

  return NUMERIC_PATH_SEGMENT_PATTERN.test(decodedSegment) ||
    UUID_PATH_SEGMENT_PATTERN.test(decodedSegment) ||
    EMAIL_PATH_SEGMENT_PATTERN.test(decodedSegment) ||
    ENCODED_PATH_FRAGMENT_PATTERN.test(decodedSegment)
    ? '__id__'
    : segment
}

export const toSafePagePath = (value: string): string => {
  const pathname = toPathname(value)
  const knownDynamicPath = KNOWN_DYNAMIC_PATHS.find(([pattern]) => pattern.test(pathname))

  if (knownDynamicPath) {
    return knownDynamicPath[1]
  }

  return pathname.split('/').map(sanitizePathSegment).join('/')
}

const TRANSPORT_PROPERTY_NAMES = ['token', 'distinct_id', '$anon_distinct_id'] as const
const COMMON_PROPERTY_NAMES = [
  '$geoip_disable',
  'app_version',
  'environment',
  'event_schema_version',
  'source',
] as const
const EVENT_PROPERTY_NAMES = {
  page_view: ['page_path', 'pathname'],
  registration_started: ['entry_point'],
  user_authenticated: ['auth_method'],
  checkout_started: ['amount_minor', 'currency', 'project_id', 'tariff_id'],
  flow_failed: ['attempt', 'error_id', 'failure_code', 'failure_stage', 'flow_id', 'http_status_group', 'is_retryable'],
} as const
const IDENTIFIED_USER_STORAGE_KEY = 'evirma_product_analytics_user_id'
const RESET_PENDING_STORAGE_KEY = 'evirma_product_analytics_reset_pending'
const RESET_PENDING_COOKIE_NAME = 'evirma_product_analytics_reset_pending'
const RESET_PENDING_COOKIE_TTL_SECONDS = 86400

const readResetPendingCookie = (): boolean =>
  document.cookie
    .split(';')
    .map((part) => part.trim())
    .some((part) => part === `${RESET_PENDING_COOKIE_NAME}=1`)

const writeResetPendingCookie = (resetPending: boolean): void => {
  const secure = location.protocol === 'https:' || process.env.NODE_ENV === 'production'
  document.cookie = [
    `${RESET_PENDING_COOKIE_NAME}=${resetPending ? '1' : ''}`,
    `Max-Age=${resetPending ? RESET_PENDING_COOKIE_TTL_SECONDS : 0}`,
    'Path=/',
    'SameSite=Lax',
    ...(secure ? ['Secure'] : []),
  ].join('; ')

  if (readResetPendingCookie() !== resetPending) {
    throw new Error('Reset-pending cookie is unavailable')
  }
}

const browserIdentityPersistence: ProductAnalyticsDependencies['identityPersistence'] = {
  clearUserId() {
    window.localStorage.removeItem(IDENTIFIED_USER_STORAGE_KEY)
  },
  getResetPending() {
    return window.localStorage.getItem(RESET_PENDING_STORAGE_KEY) === '1'
  },
  getResetPendingCookie() {
    return readResetPendingCookie()
  },
  getUserId() {
    return window.localStorage.getItem(IDENTIFIED_USER_STORAGE_KEY) || undefined
  },
  setResetPending(resetPending: boolean) {
    if (resetPending) {
      window.localStorage.setItem(RESET_PENDING_STORAGE_KEY, '1')
      return
    }
    window.localStorage.removeItem(RESET_PENDING_STORAGE_KEY)
  },
  setResetPendingCookie(resetPending: boolean) {
    writeResetPendingCookie(resetPending)
  },
  setUserId(userId: string) {
    window.localStorage.setItem(IDENTIFIED_USER_STORAGE_KEY, userId)
  },
}

const pickProperties = (
  properties: CaptureResult['properties'],
  propertyNames: readonly string[]
): CaptureResult['properties'] =>
  Object.fromEntries(
    propertyNames.flatMap((propertyName) =>
      properties[propertyName] === undefined ? [] : [[propertyName, properties[propertyName]]]
    )
  )

export const sanitizePostHogEvent: BeforeSendFn = (captureResult) => {
  if (!captureResult) {
    return null
  }

  const transportProperties = pickProperties(captureResult.properties, TRANSPORT_PROPERTY_NAMES)

  if (captureResult.event === '$identify') {
    const userId = captureResult.$set?.user_id
    if (typeof userId !== 'string' || !isInternalUserId(userId)) {
      return null
    }

    return {
      uuid: captureResult.uuid,
      event: captureResult.event,
      properties: { ...transportProperties, $geoip_disable: true },
      $set: { user_id: userId },
      ...(captureResult.timestamp === undefined ? {} : { timestamp: captureResult.timestamp }),
    }
  }

  const eventPropertyNames = EVENT_PROPERTY_NAMES[captureResult.event as keyof typeof EVENT_PROPERTY_NAMES]
  if (!eventPropertyNames) {
    return null
  }

  const properties = {
    ...transportProperties,
    ...pickProperties(captureResult.properties, COMMON_PROPERTY_NAMES),
    ...pickProperties(captureResult.properties, eventPropertyNames),
  }
  if (captureResult.event === 'page_view') {
    if (typeof properties.page_path === 'string') {
      properties.page_path = toSafePagePath(properties.page_path)
    }
    if (typeof properties.pathname === 'string') {
      properties.pathname = toSafePagePath(properties.pathname)
    }
  }

  return {
    uuid: captureResult.uuid,
    event: captureResult.event,
    properties,
    ...(captureResult.timestamp === undefined ? {} : { timestamp: captureResult.timestamp }),
  }
}

export const createProductAnalytics = ({
  captureException,
  identityPersistence,
  posthog: posthogClient,
}: ProductAnalyticsDependencies) => {
  let commonProperties: CommonEventProperties | null = null
  let errorReportingEnabled = false
  let isConfiguredForCollection = false
  let isIdentityValid = true
  let isInitialized = false
  let isRegistrationStartedCaptured = false
  let isResetPending = false

  const tryOperation = (operation: () => void): boolean => {
    try {
      operation()
      return true
    } catch {
      return false
    }
  }

  const hasPendingReset = (): boolean => {
    if (isResetPending) {
      return true
    }
    try {
      return identityPersistence.getResetPending() || identityPersistence.getResetPendingCookie()
    } catch {
      return true
    }
  }

  const canCapture = (): boolean => commonProperties !== null && isIdentityValid && !hasPendingReset()

  const capture = (eventName: string, properties: Record<string, unknown>): boolean => {
    if (!canCapture()) {
      return false
    }

    try {
      posthogClient.capture(eventName, { ...commonProperties, ...properties }, { timestamp: new Date() })
      return true
    } catch {
      return false
    }
  }

  const reset = (): boolean => {
    isResetPending = true
    isIdentityValid = false
    const isResetPendingStored = tryOperation(() => identityPersistence.setResetPending(true))
    const isResetPendingCookieStored = tryOperation(() => identityPersistence.setResetPendingCookie(true))

    if (!isConfiguredForCollection) {
      return false
    }

    const isSdkResetSuccessful = isInitialized && tryOperation(() => posthogClient.reset())
    const canClearUserId = isResetPendingStored || isResetPendingCookieStored || isSdkResetSuccessful
    const isUserIdCleared = canClearUserId && tryOperation(() => identityPersistence.clearUserId())

    if ((!isResetPendingStored && !isResetPendingCookieStored) || !isSdkResetSuccessful || !isUserIdCleared) {
      return false
    }

    const isResetPendingCleared = tryOperation(() => identityPersistence.setResetPending(false))
    const isResetPendingCookieCleared = tryOperation(() => identityPersistence.setResetPendingCookie(false))
    if (!isResetPendingCleared || !isResetPendingCookieCleared) {
      return false
    }

    isResetPending = false
    isIdentityValid = true
    isRegistrationStartedCaptured = false
    return true
  }

  return {
    init(config: ProductAnalyticsConfig): boolean {
      commonProperties = null
      errorReportingEnabled = false
      isConfiguredForCollection = isProductAnalyticsConfigured(config) && !config.isImpersonating
      isInitialized = false
      if (!isConfiguredForCollection) {
        return true
      }

      try {
        posthogClient.init(config.projectToken, {
          api_host: config.host,
          autocapture: false,
          before_send: sanitizePostHogEvent,
          capture_pageview: false,
          disable_session_recording: true,
        })
      } catch {
        return false
      }
      isInitialized = true
      commonProperties = {
        $geoip_disable: true,
        app_version: config.appVersion,
        environment: config.environment,
        event_schema_version: 1,
        source: 'web',
      }
      errorReportingEnabled = config.errorReportingEnabled
      return true
    },

    getPersistedUserId() {
      return identityPersistence.getUserId()
    },

    hasPendingReset,

    pageView(pathname: string) {
      const pagePath = toSafePagePath(pathname)
      capture('page_view', { page_path: pagePath, pathname: pagePath })
    },

    identify(userId: string) {
      if (!isInternalUserId(userId)) {
        isIdentityValid = false
        return false
      }
      if (!commonProperties || !isIdentityValid || hasPendingReset()) {
        return false
      }
      try {
        posthogClient.identify(userId, { user_id: userId })
      } catch {
        return false
      }
      try {
        identityPersistence.setUserId(userId)
      } catch {
        reset()
        return false
      }
      isIdentityValid = true
      return true
    },

    invalidateIdentity() {
      isIdentityValid = false
    },

    reset,

    registrationStarted(entryPoint: RegistrationEntryPoint) {
      if (isRegistrationStartedCaptured) {
        return
      }
      isRegistrationStartedCaptured = capture('registration_started', { entry_point: entryPoint })
    },

    userAuthenticated(authMethod: AuthMethod) {
      capture('user_authenticated', { auth_method: authMethod })
    },

    checkoutStarted({ amountMinor, currency, projectId, tariffId }: CheckoutStartedProperties) {
      capture('checkout_started', {
        amount_minor: amountMinor,
        currency,
        project_id: projectId,
        tariff_id: tariffId,
      })
    },

    flowFailed(error: unknown, properties: FlowFailedProperties) {
      if (!canCapture()) {
        return
      }

      let errorId: string | undefined = undefined
      if (errorReportingEnabled) {
        try {
          errorId = captureException(getFlowFailureCause(error))
        } catch {
          errorId = undefined
        }
      }

      capture('flow_failed', {
        ...(properties.attempt === undefined ? {} : { attempt: properties.attempt }),
        ...(errorId ? { error_id: errorId } : {}),
        failure_code: properties.failureCode,
        failure_stage: properties.failureStage,
        flow_id: properties.flowId,
        http_status_group: properties.httpStatusGroup,
        is_retryable: properties.isRetryable,
      })
    },
  }
}

export const productAnalytics = createProductAnalytics({
  captureException: Sentry.captureException,
  identityPersistence: browserIdentityPersistence,
  posthog,
})
